import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import reportCardApi, { errorMessage } from "./reportCardApi";

const EMPTY = { name: "", status: "active", sort_order: 0 };

/** Settings -> Exam Groups (Term 1, Term 2 ...). Optional for a school. */
const ExamGroupTab = ({ canEdit }) => {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setRows(await reportCardApi.examGroups.list());
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit.name?.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const body = { name: edit.name.trim(), status: edit.status, sort_order: Number(edit.sort_order) || 0 };
      if (edit.id) await reportCardApi.examGroups.update(edit.id, body);
      else await reportCardApi.examGroups.create(body);
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? Exam titles linked to it must be unlinked first.`)) return;
    try {
      await reportCardApi.examGroups.remove(row.id);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <p className="text-muted mb-0">
          Group exam titles into terms. Each group becomes one column on the report card.
          Link exam titles to a group from the Exam List screen.
        </p>
        {canEdit && <Button size="sm" onClick={() => setEdit({ ...EMPTY, sort_order: rows.length + 1 })}>New Exam Group</Button>}
      </div>

      <Table striped bordered size="sm">
        <thead>
          <tr><th style={{ width: 80 }}>Order</th><th>Name</th><th style={{ width: 110 }}>Status</th>{canEdit && <th style={{ width: 110 }}>Action</th>}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{r.sort_order}</td>
              <td>{r.name}</td>
              <td><span className={`badge bg-${r.status === "active" ? "success" : "secondary"}`}>{r.status}</span></td>
              {canEdit && (
                <td>
                  <Button size="sm" className="edit-button me-2" onClick={() => setEdit(r)}><i className="fa-regular fa-pen-to-square"></i></Button>
                  <Button size="sm" variant="danger" onClick={() => remove(r)}><i className="fa fa-trash"></i></Button>
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="text-center text-muted">No exam groups yet</td></tr>}
        </tbody>
      </Table>

      <Modal show={!!edit} onHide={() => setEdit(null)} centered backdrop="static">
        <Modal.Header closeButton><Modal.Title>{edit?.id ? "Edit" : "New"} Exam Group</Modal.Title></Modal.Header>
        <Modal.Body>
          {edit && (
            <Row className="g-3">
              <Col lg={12}>
                <Form.Label className="form-view-label">Name</Form.Label>
                <Form.Control value={edit.name} placeholder="e.g. Term 1" onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
              </Col>
              <Col lg={6}>
                <Form.Label className="form-view-label">Column Order</Form.Label>
                <Form.Control type="number" min={0} value={edit.sort_order} onChange={(e) => setEdit({ ...edit, sort_order: e.target.value })} />
              </Col>
              <Col lg={6}>
                <Form.Label className="form-view-label">Status</Form.Label>
                <Form.Select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEdit(null)}>Close</Button>
          <Button onClick={save} disabled={saving}>Save</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ExamGroupTab;
